import { Box, Typography, useTheme, styled } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ICustomerFormSchema, IFormSchemaReport } from 'src/generated-types/form-schema'
import { IMainComponent } from 'src/generated-types/main-component'
import { FlexSpinner } from 'src/components/Spinner'
import { CategorySection, IEquipmentTypeGroup, ISavedGroup } from './CategorySection'

export interface IBulkMovementChecklistsProps {
  customerId: EntityId
  schemas: ICustomerFormSchema[]
  selectedEquipments: IMainComponent[]
  isLoading?: boolean
  onChange: (equipmentReports: Map<number, IFormSchemaReport[]>, isComplete: boolean) => void
}

export const BulkMovementChecklists: React.FC<IBulkMovementChecklistsProps> = ({
  customerId,
  schemas,
  selectedEquipments,
  isLoading,
  onChange,
}) => {
  const { t } = useTranslation('checklist')
  const theme = useTheme()

  const equipmentTypeGroups = useMemo<IEquipmentTypeGroup[]>(() => {
    const typeMap = new Map<number, IEquipmentTypeGroup>()

    selectedEquipments.forEach(equipment => {
      const typeId = equipment.type?.id ?? equipment.typeId
      const typeName = equipment.type?.name ?? equipment.typeName ?? ''
      if (!typeId) return

      if (!typeMap.has(typeId)) {
        typeMap.set(typeId, {
          typeId,
          typeName,
          equipments: [],
          schemas: [],
        })
      }
      typeMap.get(typeId)!.equipments.push(equipment)
    })

    schemas.forEach(schema => {
      const typeIds = schema.mainComponentTypeIds ?? []
      if (typeIds.length === 0) {
        typeMap.forEach(group => group.schemas.push(schema))
      } else {
        typeIds.forEach(typeId => {
          if (typeMap.has(typeId)) {
            typeMap.get(typeId)!.schemas.push(schema)
          }
        })
      }
    })

    const groups = Array.from(typeMap.values())
    const groupsWithSchemas = groups.filter(g => g.schemas.length > 0)
    return groupsWithSchemas.length > 0 ? groupsWithSchemas : groups
  }, [selectedEquipments, schemas])

  const [savedGroupsMap, setSavedGroupsMap] = useState<Record<number, ISavedGroup[]>>({})

  const notifyParent = useCallback(
    (updatedGroupsMap: Record<number, ISavedGroup[]>) => {
      const equipmentReports = new Map<number, IFormSchemaReport[]>()
      let allEquipmentCovered = true

      equipmentTypeGroups.forEach(group => {
        const groups = updatedGroupsMap[group.typeId] ?? []
        const coveredIds = new Set(groups.flatMap(g => g.equipmentIds))
        if (group.equipments.some(e => !coveredIds.has(e.id as number))) {
          allEquipmentCovered = false
        }
        groups.forEach(g => {
          g.equipmentIds.forEach(equipmentId => {
            equipmentReports.set(equipmentId, g.reports)
          })
        })
      })

      onChange(equipmentReports, allEquipmentCovered && equipmentTypeGroups.length > 0)
    },
    [equipmentTypeGroups, onChange],
  )

  const handleSaveGroup = useCallback(
    (typeId: number, equipmentIds: number[], reports: IFormSchemaReport[]) => {
      setSavedGroupsMap(prev => {
        const newMap = { ...prev }
        const existingGroups = (newMap[typeId] ?? [])
          .map(g => ({
            ...g,
            equipmentIds: g.equipmentIds.filter(id => !equipmentIds.includes(id)),
          }))
          .filter(g => g.equipmentIds.length > 0)

        const newGroup: ISavedGroup = {
          id: `${typeId}-${Date.now()}-${equipmentIds.join('-')}`,
          equipmentIds,
          reports,
        }

        newMap[typeId] = [...existingGroups, newGroup]
        notifyParent(newMap)
        return newMap
      })
    },
    [notifyParent],
  )

  const handleRemoveGroup = useCallback(
    (typeId: number, groupId: string) => {
      setSavedGroupsMap(prev => {
        const newMap = { ...prev }
        newMap[typeId] = (newMap[typeId] ?? []).filter(g => g.id !== groupId)
        notifyParent(newMap)
        return newMap
      })
    },
    [notifyParent],
  )

  if (isLoading) {
    return (
      <StyledLoadingBox>
        <FlexSpinner />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('loading-checklists')}
        </Typography>
      </StyledLoadingBox>
    )
  }

  if (equipmentTypeGroups.length === 0) {
    return (
      <StyledEmptyBox>
        <Typography variant="body2" color="text.secondary">
          {t('no-movement-checklists')}
        </Typography>
      </StyledEmptyBox>
    )
  }

  return (
    <StyledBulkContainer>
      {equipmentTypeGroups.map(group => (
        <CategorySection
          key={group.typeId}
          group={group}
          savedGroups={savedGroupsMap[group.typeId] ?? []}
          onSaveGroup={(equipmentIds, reports) => handleSaveGroup(group.typeId, equipmentIds, reports)}
          onRemoveGroup={groupId => handleRemoveGroup(group.typeId, groupId)}
          customerId={customerId}
          theme={theme}
        />
      ))}
    </StyledBulkContainer>
  )
}

const StyledBulkContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
}))

const StyledLoadingBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 0',
}))

const StyledEmptyBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
}))
